/**
 * Skio Plan Picker V2 UI extension.
 * Loaded when ui-version="v2" or options.ui_version === "v2".
 */
import { html, unsafeHTML, unsafeCSS } from 'https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js'
import { SkioPlanPicker } from 'skio-plan-picker-component'
import { skioV2CssText } from 'skio-plan-picker-v2-styles'

const v2ToplineStyle =
  'display:flex;flex-direction:row;flex-wrap:nowrap;align-items:center;justify-content:flex-start;width:100%;gap:8px'
const v2PricesStyle =
  'display:flex;flex-direction:row;flex-wrap:nowrap;align-items:center;gap:4px;margin-left:auto;white-space:nowrap'
const v2StackedPricesStyle =
  'display:grid;grid-template-areas:"compare badge" "sale badge";grid-template-columns:auto auto;grid-template-rows:auto auto;align-items:center;justify-content:end;column-gap:8px;row-gap:0;margin-left:auto;white-space:nowrap'
const v2StackedCompareStyle =
  'grid-area:compare;justify-self:end;font-size:12px;line-height:14px'
const v2StackedSaleStyle =
  'grid-area:sale;justify-self:end;font-size:16px;line-height:18px'
const v2StackedSaveBadgeStyle =
  'grid-area:badge;align-self:center;border-radius:0;padding:4px 7px;font-size:10px;line-height:14px'

/** Normalizes benefits HTML strings from JSON script payloads. */
function readBenefitsHtml(value) {
  if (typeof value !== 'string') return ''
  return value.trim()
}

const v2OriginalConnectedCallback = SkioPlanPicker.prototype.connectedCallback

SkioPlanPicker.prototype.connectedCallback = async function () {
  if (this.isV2()) {
    this.applyV2Settings?.()
  }

  const targetFormId = this.getAttribute('data-target-form-id')
  if (targetFormId && (!this.options?.form_id || this.options.form_id === '' || String(this.options.form_id).includes('{{'))) {
    this.options = { ...(this.options || {}), form_id: targetFormId }
  }

  await v2OriginalConnectedCallback.call(this)

  if (this.isV2()) {
    this.ensureV2StylesInjected?.()
    this.loadV2BenefitsData?.()
    this.applyV2BenefitsForVariant?.(this.selectedVariant)
    this.requestUpdate()
    requestAnimationFrame(() => this.applyV2StackPriceDomStyles?.())
  }
}

const v2OriginalUpdated = SkioPlanPicker.prototype.updated

SkioPlanPicker.prototype.updated = function (changed) {
  if (this.isV2()) {
    this.ensureV2StylesInjected?.()
  }

  const result = v2OriginalUpdated.call(this, changed)

  if (this.isV2()) {
    this.ensureV2StylesInjected?.()
    requestAnimationFrame(() => this.applyV2StackPriceDomStyles?.())

    if (changed.has('selectedVariant')) {
      this.applyV2BenefitsForVariant?.(this.selectedVariant)
      this.requestUpdate()
    }
  }

  return result
}

const v2OriginalSelectSellingPlanGroup = SkioPlanPicker.prototype.selectSellingPlanGroup

SkioPlanPicker.prototype.selectSellingPlanGroup = function (group) {
  v2OriginalSelectSellingPlanGroup.call(this, group)

  if (this.isV2()) {
    this.requestUpdate()
  }
}

Object.assign(SkioPlanPicker.prototype, {
  isDesignMode() {
    return Boolean(this.debug || window.Shopify?.designMode)
  },

  isTrialSkioV2Template() {
    const handle = this.product?.handle || ''
    const templateHint = [
      window.location?.href || '',
      this.getAttribute('template') || '',
      this.getAttribute('data-template') || '',
    ].join(' ')

    return handle === 'anti-blemish-scar-repair-serum-15ml' || templateHint.includes('product-new-trial-skio')
  },

  usesV2StackPrices() {
    return Boolean(this.options?.stack_prices || this.isTrialSkioV2Template())
  },

  applyV2StackPriceDomStyles() {
    if (!this.isV2()) return

    const root = this.renderRoot
    if (!root) return

    const fieldset = root.querySelector('.skio-plan-picker--v2')
    if (!fieldset) return

    if (!this.usesV2StackPrices()) {
      fieldset.classList.remove('skio-v2--stack-prices')
      return
    }

    fieldset.classList.add('skio-v2--stack-prices')

    root.querySelectorAll('.skio-v2-prices').forEach((prices) => {
      prices.style.setProperty('display', 'grid', 'important')
      prices.style.setProperty('grid-template-areas', '"compare badge" "sale badge"', 'important')
      prices.style.setProperty('grid-template-columns', 'auto auto', 'important')
      prices.style.setProperty('grid-template-rows', 'auto auto', 'important')
      prices.style.setProperty('align-items', 'center', 'important')
      prices.style.setProperty('justify-content', 'end', 'important')
      prices.style.setProperty('column-gap', '8px', 'important')
      prices.style.setProperty('row-gap', '0', 'important')
      prices.style.setProperty('margin-left', 'auto', 'important')
      prices.style.setProperty('white-space', 'nowrap', 'important')
    })

    root.querySelectorAll('.skio-v2-compare').forEach((compare) => {
      compare.style.setProperty('grid-area', 'compare', 'important')
      compare.style.setProperty('justify-self', 'end', 'important')
      compare.style.setProperty('font-size', '12px', 'important')
      compare.style.setProperty('line-height', '14px', 'important')
    })

    root.querySelectorAll('.skio-v2-sale').forEach((sale) => {
      sale.style.setProperty('grid-area', 'sale', 'important')
      sale.style.setProperty('justify-self', 'end', 'important')
      sale.style.setProperty('font-size', '16px', 'important')
      sale.style.setProperty('line-height', '18px', 'important')
    })

    root.querySelectorAll('.skio-v2-save-badge').forEach((badge) => {
      badge.style.setProperty('grid-area', 'badge', 'important')
      badge.style.setProperty('align-self', 'center', 'important')
      badge.style.setProperty('border-radius', '0', 'important')
      badge.style.setProperty('padding', '4px 7px', 'important')
      badge.style.setProperty('font-size', '10px', 'important')
      badge.style.setProperty('line-height', '14px', 'important')
    })

    root.querySelectorAll('.skio-v2-gift-badge').forEach((badge) => {
      badge.style.setProperty('border-radius', '0', 'important')
    })
  },

  v2StylesTemplate() {
    return html`<style>
      ${unsafeCSS(skioV2CssText)}
    </style>`
  },

  ensureV2StylesInjected() {
    if (!this.isV2()) return

    const root = this.renderRoot
    if (!root) return

    let style = root.querySelector('#skio-v2-injected-styles')
    if (!style) {
      style = document.createElement('style')
      style.id = 'skio-v2-injected-styles'
    }

    style.textContent = skioV2CssText

    // Keep V2 stylesheet last in the shadow root so it wins over Lit's adoptedStyleSheets.
    root.appendChild(style)
  },

  applyV2Settings() {
    if (!this.isV2()) return

    this.applyTargetFormId?.()

    const v2SettingsRaw = this.getAttribute('data-v2-settings')
    if (v2SettingsRaw) {
      try {
        const v2Settings = JSON.parse(v2SettingsRaw)
        this.options = { ...(this.options || {}), ...v2Settings }
      } catch (e) {
        console.warn('[Skio V2] Could not parse data-v2-settings', e)
      }
    }

    const benefitsHtml = this.getAttribute('data-benefits-html')
    if (benefitsHtml) {
      this.options = { ...(this.options || {}), benefits_html: benefitsHtml }
    }

    if (this.usesV2StackPrices()) {
      this.options = { ...(this.options || {}), stack_prices: true }
      this.setAttribute('data-v2-stack-prices', 'true')
    } else {
      this.removeAttribute('data-v2-stack-prices')
    }

    this.loadV2BenefitsData()
    this.applyV2BenefitsForVariant(this.selectedVariant)

    if (this.options?.ui_version === 'v2') {
      this.setAttribute('ui-version', 'v2')
    }

    this.style.setProperty('--skio-v2-banner-bg', this.options?.banner_background || '#a2bfb9')
    this.style.setProperty('--skio-v2-banner-color', this.options?.banner_text_color || '#000')
    this.style.setProperty('--skio-v2-banner-border', this.options?.banner_border_color || '#000')
    this.style.setProperty('--skio-group-border-color', '#e5e5e5')
    this.style.setProperty('--skio-group-border-selected-color', '#000000')
  },

  getSavePercent(sellingPlan = this.selectedSellingPlan) {
    if (!sellingPlan) return 0
    const discount = this.discount(sellingPlan)
    const percent = discount?.percent
    if (!percent || percent === Infinity || percent < 0) return 0
    return Math.round(percent)
  },

  getSellingPlanDays(sellingPlan) {
    if (!sellingPlan) return null

    const policy = sellingPlan.delivery_policy || sellingPlan.billing_policy
    if (policy?.interval) {
      const interval = String(policy.interval).toUpperCase()
      const count = policy.interval_count || 1

      switch (interval) {
        case 'DAY':
          return count
        case 'WEEK':
          return count * 7
        case 'MONTH':
        case 'YEAR':
          return null
        default:
          break
      }
    }

    const match = sellingPlan.name?.match(/(\d+)\s*(day|week|month|year)s?/i)
    if (!match) return null

    const value = parseInt(match[1], 10)
    const unit = match[2].toLowerCase()

    if (unit === 'day') return value
    if (unit === 'week') return value * 7
    return null
  },

  /**
   * Days supply for price-per-day: variant ml at 1 ml/day (matches merchant spreadsheet).
   * Optional override via options.variant_days_supply[variantId] (e.g. from metafield).
   */
  getVariantDaysSupply() {
    const variant = this.selectedVariant
    if (!variant) return null

    const map = this.options?.variant_days_supply
    if (map && variant.id != null) {
      const fromMap = map[String(variant.id)] ?? map[variant.id]
      const days = parseInt(fromMap, 10)
      if (days > 0) return days
    }

    const sources = [variant.option1, variant.option2, variant.option3, variant.title, variant.public_title, variant.name]
    for (const src of sources) {
      if (!src) continue
      const match = String(src).match(/(\d+(?:[.,]\d+)?)\s*ml\b/i)
      if (!match) continue
      const ml = parseFloat(match[1].replace(',', '.'))
      if (ml > 0) return Math.round(ml)
    }

    return null
  },

  getPricePerDayText(sellingPlan = this.selectedSellingPlan) {
    if (!this.options?.show_price_per_day || !sellingPlan) return null

    const days = this.getVariantDaysSupply() ?? this.getSellingPlanDays(sellingPlan)
    if (!days || days <= 0) return null

    const priceCents = this.price(sellingPlan, false)
    const perDayCents = Math.round(priceCents / days)

    const prefix = this.options?.price_per_day_prefix || 'Clearer skin for just'
    const suffix = this.options?.price_per_day_suffix || 'a day'

    return `${prefix} ${this.money(perDayCents)} ${suffix}`
  },

  getOneTimePricePerDayText() {
    if (!this.options?.show_onetime_price_per_day || !this.selectedVariant?.price) return null

    const days = this.getVariantDaysSupply()
    if (!days || days <= 0) return null

    const perDayCents = Math.round(this.selectedVariant.price / days)
    const prefix = this.options?.price_per_day_prefix || 'Clearer skin for just'
    const suffix = this.options?.price_per_day_suffix || 'a day'

    return `${prefix} ${this.money(perDayCents)} ${suffix}`
  },

  v2CheckIcon() {
    return html`<svg width="11" height="8" viewBox="0 0 11 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M4.44651e-05 4.23136L1.40203 2.8244L3.70388 5.08647L8.8097 0.000532746L10.2216 1.40749L3.70388 7.89045L4.44651e-05 4.23136Z" fill="#000"/>
    </svg>`
  },

  loadV2BenefitsData() {
    if (this._v2BenefitsDataLoaded) return

    const readJsonScript = (el) => {
      if (!el?.textContent) return null
      try {
        return JSON.parse(el.textContent)
      } catch (error) {
        console.warn('[Skio V2] Could not parse benefits JSON', error)
        return null
      }
    }

    const byVariantScript =
      this.querySelector('[data-skio-v2-benefits-by-variant]') ||
      this.parentElement?.querySelector('[data-skio-v2-benefits-by-variant]')
    const byVariantData = readJsonScript(byVariantScript)

    if (byVariantData && typeof byVariantData === 'object') {
      this._v2BenefitsByVariant = {
        default: readBenefitsHtml(byVariantData.default),
        variants: byVariantData.variants && typeof byVariantData.variants === 'object' ? byVariantData.variants : {},
      }
      this._v2BenefitsDataLoaded = true
      return
    }

    const inlineScript = this.querySelector('[data-skio-v2-benefits]')
    const inlineParsed = readJsonScript(inlineScript)
    const inlineHtml = readBenefitsHtml(inlineParsed)

    if (inlineHtml) {
      this._v2BenefitsByVariant = { default: inlineHtml, variants: {} }
      this._v2BenefitsDataLoaded = true
      return
    }

    const template = this.querySelector('#skio-v2-benefits-template')
    if (template) {
      const templateHtml = (
        template.innerHTML ||
        template.content?.firstElementChild?.outerHTML ||
        Array.from(template.content?.childNodes || [])
          .map(node => node.textContent || node.outerHTML || '')
          .join('')
      ).trim()

      if (templateHtml) {
        this._v2BenefitsByVariant = { default: templateHtml, variants: {} }
        this._v2BenefitsDataLoaded = true
        return
      }
    }

    const benefitsId = this.getAttribute('data-benefits-id')
    if (benefitsId) {
      const jsonEl = document.getElementById(benefitsId)
      const externalParsed = readJsonScript(jsonEl)
      const externalHtml = readBenefitsHtml(externalParsed)
      if (externalHtml) {
        this._v2BenefitsByVariant = { default: externalHtml, variants: {} }
      }
    }

    this._v2BenefitsDataLoaded = true
  },

  getV2BenefitsHtmlForVariant(variant) {
    this.loadV2BenefitsData()

    const variantId = variant?.id != null ? String(variant.id) : ''
    const map = this._v2BenefitsByVariant

    if (map) {
      const variantHtml = readBenefitsHtml(map.variants?.[variantId])
      if (variantHtml) return variantHtml
      return readBenefitsHtml(map.default)
    }

    return ''
  },

  applyV2BenefitsForVariant(variant) {
    const benefitsHtml = this.getV2BenefitsHtmlForVariant(variant || this.selectedVariant)
    const previousHtml = this.options?.benefits_html

    this.options = { ...(this.options || {}), benefits_html: benefitsHtml }

    if (benefitsHtml !== previousHtml) {
      this.requestUpdate()
    }
  },

  loadV2BenefitsFromTemplate() {
    this.loadV2BenefitsData()
    this.applyV2BenefitsForVariant(this.selectedVariant)
  },

  getV2ComparePrice(plan) {
    const oneTimePrice = this.selectedVariant?.price
    if (!oneTimePrice || !plan) return null

    const subscriptionPrice = this.price(plan, false)
    if (subscriptionPrice >= oneTimePrice) return null

    return oneTimePrice
  },

  syncVariantFromEvent(variant) {
    if (!variant) return

    if (this.product?.variants?.length) {
      this.selectedVariant = this.product.variants.find(v => String(v.id) === String(variant.id)) || variant
    } else {
      this.selectedVariant = variant
    }

    this.variantChanged = true
    this.applyV2BenefitsForVariant?.(this.selectedVariant)
  },

  normalizeV2BenefitsHtml(html) {
    if (!html || typeof html !== 'string') return html

    try {
      const template = document.createElement('template')
      template.innerHTML = html.trim()

      template.content.querySelectorAll('li, p').forEach((line) => {
        if (line.querySelector(':scope > .skio-v2-benefit-text')) return

        const wrapper = document.createElement('span')
        wrapper.className = 'skio-v2-benefit-text'

        while (line.firstChild) {
          wrapper.appendChild(line.firstChild)
        }

        line.appendChild(wrapper)
      })

      return template.innerHTML
    } catch (error) {
      console.warn('[Skio V2] Could not normalize benefits HTML', error)
      return html
    }
  },

  renderV2Benefits() {
    this.applyV2BenefitsForVariant(this.selectedVariant)
    const benefitsHtml = this.options?.benefits_html
    if (!benefitsHtml || benefitsHtml.trim() === '') return null

    const normalizedHtml = this.normalizeV2BenefitsHtml(benefitsHtml)

    return html`
      <div class="skio-v2-benefits">${unsafeHTML(normalizedHtml)}</div>
    `
  },

  renderV2Gift({ image, title, description, badge }) {
    const badgeStyle = this.usesV2StackPrices() ? 'border-radius:0' : ''

    return html`
      <div class="skio-v2-gift">
        ${image
          ? html`<img class="skio-v2-gift-image" src="${image}" alt="" width="47" height="47" loading="lazy">`
          : html`<span class="skio-v2-gift-image skio-v2-gift-image--placeholder" aria-hidden="true"></span>`}
        <div class="skio-v2-gift-body">
          ${title ? html`<div class="skio-v2-gift-title">${title}</div>` : ''}
          ${description ? html`<div class="skio-v2-gift-desc">${description}</div>` : ''}
        </div>
        <span class="skio-v2-gift-badge" style="${badgeStyle}">${badge || 'FREE'}</span>
      </div>
    `
  },

  /** Gifts from the free-gift rules for one purchase type: 'subscription' or 'one_time'. */
  renderV2Gifts(purchaseType) {
    // The picker starts on the product's first variant and ignores ?variant=, so the form's id wins
    const variantId = this.form?.querySelector('[name="id"]')?.value || this.selectedVariant?.id
    const variantGid = `gid://shopify/ProductVariant/${variantId}`
    const productGid = `gid://shopify/Product/${this.product?.id}`
    const gifts = (this.options?.gifts || []).filter(
      (gift) =>
        (gift.purchaseType === 'both' || gift.purchaseType === purchaseType) &&
        (!gift.triggerIds?.length || gift.triggerIds.includes(variantGid) || gift.triggerIds.includes(productGid))
    )
    if (!gifts.length) return null

    return html`<div class="skio-v2-gifts">${gifts.map((gift) => this.renderV2Gift(gift))}</div>`
  },

  findV2CancelPolicyButton() {
    const selectors = [
      '#shopify-subscription-policy-button',
      '[id="shopify-subscription-policy-button"]',
      '[aria-controls="shopify-subscription-policy"]',
      '[data-shopify-subscription-policy-button]',
    ]
    const roots = [this.getRootNode?.(), document].filter(Boolean)

    for (const root of roots) {
      for (const selector of selectors) {
        const button = root.querySelector?.(selector)
        if (button) return button
      }
    }

    for (const element of document.querySelectorAll('*')) {
      if (!element.shadowRoot) continue

      for (const selector of selectors) {
        const button = element.shadowRoot.querySelector(selector)
        if (button) return button
      }
    }

    return null
  },

  openV2CancelPolicy(event) {
    event?.preventDefault()
    event?.stopPropagation()

    const policyButton = this.findV2CancelPolicyButton()
    if (!policyButton) return

    policyButton.dispatchEvent(new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      composed: true,
      view: window,
    }))
  },

  handleV2CancelPolicyKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return

    this.openV2CancelPolicy(event)
  },

  renderV2CancelPolicy() {
    if (!this.options?.show_cancel_policy) return null

    const hasPolicyButton = Boolean(this.findV2CancelPolicyButton())

    return html`
      <div class="skio-v2-cancel-policy" style="display:flex;align-items:flex-start;gap:8px;margin:8px 0 0;padding:0;font-family:Montserrat,sans-serif;font-weight:400;font-size:12px;line-height:16.41px;color:#000">
        <svg class="skio-v2-cancel-policy-icon" style="display:block;flex:0 0 17px;width:17px;height:17px;margin-top:0" width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M6.43799 2.49002C7.63799 1.89002 8.03799 1.29002 8.43799 0.890015C8.83799 1.29002 9.23799 1.89002 10.438 2.49002C11.638 3.09002 12.238 2.89002 13.238 3.29002C14.238 3.69002 14.438 4.29002 14.438 5.49002C14.438 6.69002 14.238 9.69002 13.838 10.89C13.438 12.09 10.838 14.49 8.43799 15.29C6.03799 14.49 3.43799 12.09 3.03799 10.89C2.63799 9.69002 2.43799 6.69002 2.43799 5.49002C2.43799 4.29002 2.63799 3.69002 3.63799 3.29002C4.63799 2.89002 5.23799 3.09002 6.43799 2.49002Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M6.43799 8.34546L7.79006 9.58997L10.438 6.58997" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span style="display:inline;flex:1 1 auto;min-width:0;font:inherit;line-height:inherit">
          Edit or
          ${hasPolicyButton
            ? html`
                <span
                  class="skio-v2-cancel-policy-link"
                  role="button"
                  tabindex="0"
                  @click=${(event) => this.openV2CancelPolicy(event)}
                  @keydown=${(event) => this.handleV2CancelPolicyKeydown(event)}
                >Cancel</span>
              `
            : html`<span class="skio-v2-cancel-policy-text">Cancel</span>`}
          anytime.
        </span>
      </div>
    `
  },

  renderV2SubscriptionCard(group, index) {
    const isSelected = this.selectedSellingPlanGroup === group
    const plan = group.selected_selling_plan
    const savePercent = this.getSavePercent(plan)
    const compareAt = this.getV2ComparePrice(plan)
    const stackPrices = this.usesV2StackPrices()
    const pricesStyle = stackPrices ? v2StackedPricesStyle : v2PricesStyle
    const compareStyle = stackPrices ? v2StackedCompareStyle : ''
    const saleStyle = stackPrices ? v2StackedSaleStyle : ''
    const saveBadgeStyle = stackPrices ? v2StackedSaveBadgeStyle : ''

    const showBanner = this.options?.show_top_banner && index === 0

    const benefits = this.renderV2Benefits()
    const gifts = this.renderV2Gifts('subscription')
    const cancelPolicy = this.renderV2CancelPolicy()
    const footer = this.renderV2SubscriptionFooter()
    let benefitsAndGiftsBlock = null
    if (benefits || gifts || cancelPolicy || footer) {
      benefitsAndGiftsBlock = html`
        <div class="skio-v2-divider"></div>
        ${benefits}
        ${cancelPolicy}
        ${gifts}
        ${footer}
      `
    }

    return html`
      <div class="skio-v2-subscription-wrap">
        ${showBanner
          ? html`<div class="skio-v2-banner">${this.options?.banner_text || 'MOST POPULAR & BEST VALUE'}</div>`
          : ''}

        <div
          class="group-container ${isSelected ? 'group-container--selected' : ''}"
          @click=${() => this.selectSellingPlanGroup(group)}
        >
          <input
            id="group-${index}-${this.key}"
            class="group-input"
            name="skio-group-${this.key}"
            type="radio"
            value="${group.id}"
            @change=${() => this.selectSellingPlanGroup(group)}
            ?checked=${isSelected}
          />

          <label class="group-label" for="group-${index}-${this.key}">
            <div class="skio-v2-header">
              <div class="skio-v2-topline" style="${v2ToplineStyle}">
                ${this.options?.show_radio_selector ? this.radioTemplate() : ''}
                <span class="skio-v2-title">
                  ${group.name !== 'Prepaid' && this.options?.subscription_title
                    ? this.options.subscription_title
                    : group.name === 'Prepaid' && this.options?.prepaid_title
                    ? this.options.prepaid_title
                    : group.name}
                </span>
                <div class="skio-v2-prices" style="${pricesStyle}">
                  ${compareAt ? html`<span class="skio-v2-compare" style="${compareStyle}">${this.money(compareAt)}</span>` : ''}
                  <span class="skio-v2-sale" style="${saleStyle}">${this.price(plan)}</span>
                  ${savePercent > 0 ? html`<span class="skio-v2-save-badge" style="${saveBadgeStyle}">SAVE ${savePercent}%</span>` : ''}
                </div>
              </div>

              ${this.getPricePerDayText(plan)
                ? html`<div class="skio-v2-price-per-day">${this.getPricePerDayText(plan)}</div>`
                : ''}

              ${this.renderV2Subtitle(this.options?.subscription_subtitle)}
            </div>

            ${benefitsAndGiftsBlock}
          </label>
        </div>
      </div>
    `
  },

  renderV2Subtitle(text) {
    const value = typeof text === 'string' ? text.trim() : ''
    if (!value) return null
    return html`<div class="skio-v2-subtitle">${value}</div>`
  },

  renderV2OnetimeBenefits() {
    const benefitsHtml = this.options?.onetime_benefits_html
    if (!benefitsHtml || String(benefitsHtml).trim() === '') return null

    const normalizedHtml = this.normalizeV2BenefitsHtml(String(benefitsHtml))

    return html`
      <div class="skio-v2-divider"></div>
      <div class="skio-v2-benefits skio-v2-benefits--onetime">${unsafeHTML(normalizedHtml)}</div>
    `
  },

  renderV2SubscriptionFooter() {
    const footerHtml = this.options?.subscription_footer_html
    if (!footerHtml || String(footerHtml).trim() === '') return null

    return html`<div class="skio-v2-card-footer">${unsafeHTML(String(footerHtml))}</div>`
  },

  renderV2Onetime() {
    if (this.product.requires_selling_plan) return null

    const isSelected = !this.selectedSellingPlanGroup
    const onetimeBenefits = this.renderV2OnetimeBenefits()

    return html`
      <div
        class="group-container skio-v2-onetime ${isSelected ? 'group-container--selected' : ''}"
        @click=${() => this.selectSellingPlanGroup(null)}
      >
        <input
          id="one-time-${this.key}"
          class="group-input"
          name="skio-group-${this.key}"
          type="radio"
          value="One time purchase"
          ?checked=${isSelected}
          @change=${() => this.selectSellingPlanGroup(null)}
        />

        <label class="group-label" for="one-time-${this.key}">
          <div class="skio-v2-header">
            <div class="skio-v2-topline skio-v2-topline--onetime" style="${v2ToplineStyle}">
              ${this.options?.show_radio_selector ? this.radioTemplate() : ''}
              <span class="skio-v2-onetime-title">${this.options?.onetime_title || 'One-time purchase'}</span>
              <span class="skio-v2-onetime-price" style="margin-left:auto">${this.money(this.selectedVariant.price)}</span>
            </div>

            ${this.getOneTimePricePerDayText()
              ? html`<div class="skio-v2-price-per-day skio-v2-price-per-day--onetime">${this.getOneTimePricePerDayText()}</div>`
              : ''}

            ${this.renderV2Subtitle(this.options?.onetime_subtitle)}
          </div>
          ${onetimeBenefits}
          ${this.renderV2Gifts('one_time')}
        </label>
      </div>
    `
  },

  renderV2DesignPreview() {
    this.ensureV2StylesInjected()
    const opts = this.options || {}
    const variantPrice = this.selectedVariant?.price
    const stackPrices = this.usesV2StackPrices()
    const v2Classes = `skio-plan-picker skio-plan-picker--v2 ${stackPrices ? 'skio-v2--stack-prices' : ''}`.trim()
    const pricesStyle = stackPrices ? v2StackedPricesStyle : v2PricesStyle
    const saleStyle = stackPrices ? v2StackedSaleStyle : ''
    const saveBadgeStyle = stackPrices ? v2StackedSaveBadgeStyle : ''
    const previewSubscription = html`
      <div class="skio-v2-subscription-wrap">
        ${opts.show_top_banner
          ? html`<div class="skio-v2-banner">${opts.banner_text || 'MOST POPULAR & BEST VALUE'}</div>`
          : ''}
        <div class="group-container group-container--selected">
          <label class="group-label">
            <div class="skio-v2-topline" style="${v2ToplineStyle}">
              ${opts.show_radio_selector !== false ? this.radioTemplate() : ''}
              <span class="skio-v2-title">${opts.subscription_title || 'Subscription'}</span>
              <div class="skio-v2-prices" style="${pricesStyle}">
                ${variantPrice
                  ? html`<span class="skio-v2-sale" style="${saleStyle}">${this.money(variantPrice)}</span>`
                  : html`<span class="skio-v2-sale" style="${saleStyle}">—</span>`}
                <span class="skio-v2-save-badge" style="${saveBadgeStyle}">SAVE 10%</span>
              </div>
            </div>
            ${opts.show_price_per_day
              ? html`<div class="skio-v2-price-per-day">${opts.price_per_day_prefix || 'Clearer skin for just'} — ${opts.price_per_day_suffix || 'a day'}</div>`
              : ''}
            ${this.renderV2Benefits() || this.renderV2Gifts('subscription')
              ? html`
                  <div class="skio-v2-divider"></div>
                  ${this.renderV2Benefits()}
                  ${this.renderV2CancelPolicy()}
                  ${this.renderV2Gifts('subscription')}
                `
              : this.renderV2CancelPolicy()
              ? html`
                  <div class="skio-v2-divider"></div>
                  ${this.renderV2CancelPolicy()}
                `
              : ''}
          </label>
        </div>
      </div>
    `
    const previewOnetime = html`
      <div class="group-container skio-v2-onetime">
        <label class="group-label">
          <div class="skio-v2-topline skio-v2-topline--onetime" style="${v2ToplineStyle}">
            ${opts.show_radio_selector !== false ? this.radioTemplate() : ''}
            <span class="skio-v2-onetime-title">${opts.onetime_title || 'One-time purchase'}</span>
            <span class="skio-v2-onetime-price" style="margin-left:auto">${variantPrice ? this.money(variantPrice) : '—'}</span>
          </div>
          ${this.getOneTimePricePerDayText()
            ? html`<div class="skio-v2-price-per-day skio-v2-price-per-day--onetime">${this.getOneTimePricePerDayText()}</div>`
            : ''}
        </label>
      </div>
    `

    return html`
      <fieldset class="${v2Classes}" role="radiogroup" aria-label="Subscription preview">
        ${opts.onetime_first ? previewOnetime : ''}
        ${previewSubscription}
        ${!opts.onetime_first ? previewOnetime : ''}
      </fieldset>
    `
  },

  renderV2() {
    this.applyV2Settings?.()
    this.ensureV2StylesInjected()

    const designMode = this.isDesignMode()
    const hasSellingPlans = Array.isArray(this.availableSellingPlanGroups) && this.availableSellingPlanGroups.length > 0
    const hasAllocations = Boolean(this.selectedVariant?.selling_plan_allocations?.length)

    if (designMode && (!this.viable() || !hasAllocations || !hasSellingPlans)) {
      return this.renderV2DesignPreview()
    }

    if (!this.viable() || this.options?.layout === 'hidden') {
      return designMode ? this.renderV2DesignPreview() : ''
    }

    if (!hasAllocations && !this.options?.show_without_subscription) {
      return designMode ? this.renderV2DesignPreview() : ''
    }

    if (!hasSellingPlans && !this.options?.show_without_subscription) {
      return designMode ? this.renderV2DesignPreview() : ''
    }

    const subscriptionCards = this.availableSellingPlanGroups?.length
      ? this.availableSellingPlanGroups.map((group, index) => this.renderV2SubscriptionCard(group, index))
      : []

    const onetimeFirst = this.options?.onetime_first
    const v2Classes = `skio-plan-picker skio-plan-picker--v2 ${this.usesV2StackPrices() ? 'skio-v2--stack-prices' : ''}`.trim()

    return html`
      ${this.debug ? this.setupMode() : null}
      <fieldset class="${v2Classes}" role="radiogroup" aria-labelledby="skio-plan-picker-legend">
        <legend id="skio-plan-picker-legend" class="sr-only">${unsafeHTML(this.options?.legend_content || 'Select purchase option')}</legend>
        ${onetimeFirst ? this.renderV2Onetime() : ''}
        ${subscriptionCards}
        ${!onetimeFirst ? this.renderV2Onetime() : ''}
      </fieldset>
    `
  },
})

window.__skioV2ExtensionLoaded = true

function bootV2Pickers() {
  document.querySelectorAll('skio-plan-picker[ui-version="v2"]').forEach(picker => {
    try {
      picker.applyTargetFormId?.()
      picker.applyV2Settings?.()
      picker.ensureV2StylesInjected?.()

      if (typeof picker.bindFormEvents === 'function') {
        picker.bindFormEvents()
      }

      picker.requestUpdate()
    } catch (error) {
      console.error('[Skio V2] Failed to boot picker', error)
    }
  })
}

bootV2Pickers()
requestAnimationFrame(bootV2Pickers)

document.addEventListener('variant:change', event => {
  if (!event.detail?.variant) return

  document.querySelectorAll('skio-plan-picker[ui-version="v2"]').forEach(picker => {
    picker.syncVariantFromEvent?.(event.detail.variant)
    picker.requestUpdate()
  })
})

export {}
